// Deterministic avatar generator per GDD §11.4.
// Input: display name (avatar_seed). Output: a CanvasTexture of a 128x128 rounded-square
// sprite with first-2-capital-letters centered over a palette-hashed background.

import { CanvasTexture, LinearFilter, SRGBColorSpace, type Texture } from 'three';
import { paletteFor } from '../scene/lib/util.js';

const SIZE = 128;
const cache = new Map<string, Texture>();

export interface AvatarOpts {
  size?: number;
  platformBadge?: 'twitch' | 'youtube' | 'kick' | null;
}

export function initialsFrom(name: string): string {
  const caps = Array.from(name).filter((ch) => /[A-Z]/.test(ch));
  if (caps.length >= 2) return `${caps[0] ?? ''}${caps[1] ?? ''}`;
  const alpha = Array.from(name).filter((ch) => /[A-Za-z]/.test(ch));
  const pair = alpha.slice(0, 2).join('').toUpperCase();
  if (pair.length === 2) return pair;
  return (pair + '??').slice(0, 2);
}

export function avatarTexture(seed: string, opts: AvatarOpts = {}): Texture {
  const size = opts.size ?? SIZE;
  const badge = opts.platformBadge ?? null;
  const key = `${seed}|${size}|${badge ?? ''}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  // Rounded square.
  const r = size * 0.18;
  const bg = paletteFor(seed);
  ctx.fillStyle = bg;
  roundRect(ctx, { x: 0, y: 0, w: size, h: size, r });
  ctx.fill();

  // Initials.
  const initials = initialsFrom(seed);
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${Math.floor(size * 0.5)}px 'Inter', system-ui, sans-serif`;
  ctx.fillText(initials, size / 2, size / 2 + size * 0.03);

  // Platform badge.
  if (badge) {
    const bsize = size * 0.3;
    const bx = size - bsize - size * 0.05;
    const by = size - bsize - size * 0.05;
    const bcol = badge === 'twitch' ? '#9147FF' : badge === 'youtube' ? '#FF0033' : '#53FC18';
    ctx.fillStyle = bcol;
    roundRect(ctx, { x: bx, y: by, w: bsize, h: bsize, r: bsize * 0.22 });
    ctx.fill();
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `bold ${Math.floor(bsize * 0.75)}px 'Inter', system-ui, sans-serif`;
    ctx.fillText(badge[0]!.toUpperCase(), bx + bsize / 2, by + bsize / 2 + bsize * 0.06);
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  cache.set(key, tex);
  return tex;
}

interface RoundRectBox {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

function roundRect(ctx: CanvasRenderingContext2D, b: RoundRectBox): void {
  const { x, y, w, h, r } = b;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function clearAvatarCache(): void {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}
